import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import {
  COUNTRIES,
  forgotPasswordSchema,
  loginSchema,
  PASSWORD_MIN,
  registerSchema,
  resetPasswordSchema,
  type Me,
} from '@trackflow/shared';
import { apiPost, ApiRequestError } from '../../lib/api';
import { safeNext, useMe, useSetMe } from '../../lib/auth';
import { useZodForm } from '../../lib/form';
import { countryName } from '../../lib/format';
import { Button, ButtonLink } from '../../ui/Button';
import { SelectField, TextField } from '../../ui/Field';
import { Alert } from '../../ui/Card';
import { Spinner } from '../../ui/Spinner';
import { AuthLayout, FormError } from './AuthLayout';

function guessCountry() {
  const region = navigator.language.split('-')[1]?.toUpperCase();
  return region && (COUNTRIES as readonly string[]).includes(region) ? region : 'IN';
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useZodForm(loginSchema, { email: '', password: '' });

  const onSubmit = form.handleSubmit(async (data) => {
    const me = await apiPost<Me>('/auth/login', data);
    setMe(me);
    navigate(
      safeNext(
        params.get('next'),
        me.merchant ? '/dashboard' : me.user.isPlatformAdmin ? '/admin' : '/settings',
      ),
      { replace: true },
    );
  });

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Your shipments missed you."
      footer={
        <>
          New to TrackFlow?{' '}
          <Link to="/register" className="font-medium text-brand-700 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {params.get('expired') && (
          <Alert tone="info">Your session ended. Please sign in again.</Alert>
        )}
        {params.get('reset') && (
          <Alert tone="success">Password updated. Sign in with your new password.</Alert>
        )}
        <FormError message={form.formError} />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          {...form.field('email')}
        />
        <div>
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            {...form.field('password')}
          />
          <Link
            to="/forgot-password"
            className="mt-2 inline-block text-sm text-brand-700 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <Button type="submit" loading={form.submitting} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setMe = useSetMe();
  const form = useZodForm(registerSchema, {
    name: '',
    email: '',
    password: '',
    storeName: '',
    country: guessCountry(),
  });
  const countries = [...COUNTRIES].sort((a, b) => countryName(a).localeCompare(countryName(b)));

  const onSubmit = form.handleSubmit(async (data) => {
    const me = await apiPost<Me>('/auth/register', data);
    setMe(me);
    navigate('/dashboard?welcome=1', { replace: true });
  });

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Free for your first 50 shipments a month. No card needed."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-700 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={form.formError} />
        <TextField label="Your name" autoComplete="name" autoFocus {...form.field('name')} />
        <TextField label="Work email" type="email" autoComplete="email" {...form.field('email')} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          hint={`At least ${PASSWORD_MIN} characters. A short phrase works well.`}
          {...form.field('password')}
        />
        <TextField
          label="Store name"
          autoComplete="organization"
          hint="Shown on your customers' tracking page."
          {...form.field('storeName')}
        />
        <SelectField
          label="Country"
          hint="Sets your currency and payment options."
          {...form.field('country')}
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {countryName(c)}
            </option>
          ))}
        </SelectField>
        <Button type="submit" loading={form.submitting} className="w-full">
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const form = useZodForm(forgotPasswordSchema, { email: '' });
  const onSubmit = form.handleSubmit(async (data) => {
    await apiPost('/auth/forgot-password', data);
    setSent(true);
  });

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to choose a new one."
      footer={
        <Link to="/login" className="font-medium text-brand-700 hover:underline">
          ← Back to sign in
        </Link>
      }
    >
      {sent ? (
        <Alert tone="success">
          If an account exists for <strong>{form.values.email}</strong>, a reset link is on its way.
          It expires in one hour.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-5">
          <FormError message={form.formError} />
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            {...form.field('email')}
          />
          <Button type="submit" loading={form.submitting} className="w-full">
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const form = useZodForm(resetPasswordSchema, { token, password: '' });
  // The token is now in form state; keep it out of the address bar, history and screenshots.
  useEffect(() => {
    if (token) window.history.replaceState(null, '', '/reset-password');
  }, [token]);
  const onSubmit = form.handleSubmit(async (data) => {
    await apiPost('/auth/reset-password', data);
    navigate('/login?reset=1', { replace: true });
  });

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="You'll be signed out on every other device."
    >
      {!token ? (
        <Alert
          tone="error"
          action={
            <ButtonLink to="/forgot-password" size="sm" variant="secondary">
              Request a new link
            </ButtonLink>
          }
        >
          This reset link is incomplete.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-5">
          <FormError message={form.formError} />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            autoFocus
            hint={`At least ${PASSWORD_MIN} characters.`}
            {...form.field('password')}
          />
          <Button type="submit" loading={form.submitting} className="w-full">
            Update password
          </Button>
          {form.formError && (
            <Link
              to="/forgot-password"
              className="block text-center text-sm text-brand-700 hover:underline"
            >
              Request a new link
            </Link>
          )}
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { data: me, refetch } = useMe();
  const started = useRef(false);
  const verify = useMutation({
    mutationFn: () => apiPost('/auth/verify-email', { token }),
    onSuccess: () => void refetch(),
  });

  useEffect(() => {
    // Strip the token from the address bar/history once we've read it.
    window.history.replaceState(null, '', '/verify-email');
    if (token && !started.current) {
      started.current = true;
      verify.mutate();
    }
  }, [token, verify]);

  const next = me ? '/dashboard' : '/login';
  return (
    <AuthLayout title="Confirm your email">
      {!token ? (
        <Alert tone="error">This confirmation link is incomplete.</Alert>
      ) : verify.isPending || verify.isIdle ? (
        <div className="flex items-center gap-3 text-ink-600">
          <Spinner /> Confirming…
        </div>
      ) : verify.isSuccess ? (
        <div className="space-y-5">
          <Alert tone="success">Your email is confirmed. You're all set.</Alert>
          <ButtonLink to={next} className="w-full">
            Continue
          </ButtonLink>
        </div>
      ) : (
        <div className="space-y-5">
          <Alert tone="error">
            {verify.error instanceof ApiRequestError
              ? verify.error.message
              : 'Something went wrong.'}
          </Alert>
          <ButtonLink to={me ? '/settings' : '/login'} variant="secondary" className="w-full">
            {me ? 'Send a new link from Settings' : 'Sign in to request a new link'}
          </ButtonLink>
        </div>
      )}
    </AuthLayout>
  );
}
